import React, { useState, useEffect } from "react"
import Button from "@material-ui/core/Button"
import CssBaseline from "@material-ui/core/CssBaseline"
import TextField from "@material-ui/core/TextField"
import Grid from "@material-ui/core/Grid"
import Typography from "@material-ui/core/Typography"
import { makeStyles } from "@material-ui/core/styles"
import Container from "@material-ui/core/Container"
import { FormControl } from "@material-ui/core"
import AccountCircle from "@material-ui/icons/AccountCircle"
import InputAdornment from "@material-ui/core/InputAdornment"
import OutlinedInput from "@material-ui/core/OutlinedInput"
import InputLabel from "@material-ui/core/InputLabel"
import LockIcon from "@material-ui/icons/Lock"
import IconButton from "@material-ui/core/IconButton"
import Visibility from "@material-ui/icons/Visibility"
import VisibilityOff from "@material-ui/icons/VisibilityOff"
import FormHelperText from "@material-ui/core/FormHelperText"
import RequestSnackbar from "@/components/common/RequestSnackbar"
import Loading from "@/components/common/Loading"
import { userLoginRequest } from "@/redux/actions/userAction"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate, Link as RouterLink } from "react-router-dom"
import useTopRoleIsAccountHolder from "@/hooks/useTopRoleIsAccountHolder"
import useRoleIsSuperAdmin from "@/hooks/useRoleIsSuperAdmin"
import { userGetRoles } from "@/redux/actions/userRoleAction"
import { urlPath } from "@/utils"

const useStyles = makeStyles((theme) => ({
    paper: {
        marginTop: theme.spacing(8),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    avatar: {
        margin: theme.spacing(1),
        backgroundColor: theme.palette.secondary.main,
    },
    form: {
        width: "100%", // Fix IE 11 issue.
        marginTop: theme.spacing(1),
    },
    submit: {
        margin: theme.spacing(3, 0, 2),
    },
    centerText: {
        textAlign: "center",
    },
    inputField: {
        marginTop: "10px",
        marginBottom: "10px",
    },
}))

export default function Login() {
    const [username, setUsername] = useState("")
    const [password, setPassword] = useState("")
    const [errorMsg, setErrorMsg] = useState({})
    const [showPassword, setShowPassword] = useState(false)
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [requestSuccessStatus, setRequestSuccessStatus] = useState(false)
    const [responseMessage, setResponseMessage] = useState("")

    const navigate = useNavigate()

    const dispatch = useDispatch()
    const userData = useSelector((state) => state.userReducer)
    const { loading } = userData
    const classes = useStyles()

    const handleSubmit = (e) => {
        e.preventDefault()
        if (!username)
            setErrorMsg((prevErrorMsg) => ({
                ...prevErrorMsg,
                username: "Please input username or email",
            }))
        if (!password)
            setErrorMsg((prevErrorMsg) => ({
                ...prevErrorMsg,
                password: "Please input password",
            }))
        if (errorMsg.username === "" && errorMsg.password === "") {
            dispatch(userLoginRequest({ username, password }))
            setSnackbarOpen(true)
        }
    }

    useEffect(() => {
        if (userData && userData.user.userId) {
            dispatch(userGetRoles(userData.user.userId))
        }
    }, [userData])
    const topRoleIsAccountHolder = useTopRoleIsAccountHolder(userData)
    const isSuperAdmin = useRoleIsSuperAdmin(userData)

    useEffect(() => {
        if (userData && userData.user !== null && Object.keys(userData.user).length !== 0 && userData.user.constructor === Object) {
            // super admin go to admin dashboard
            if (isSuperAdmin) {
                navigate(urlPath("super-admin-main"))
            } else if (topRoleIsAccountHolder !== null) {
                // account holder to account holder dashboard; project manager and project sponsor go to project dashboard
                if (topRoleIsAccountHolder) {
                    navigate(urlPath("account-holder-main", userData.user.userId))
                } else {
                    navigate(urlPath("dashboard", userData.user.userId))
                }
            }
        }
        const detectUserChange = () => {
            if (userData) {
                if (userData.successStatus) {
                    setRequestSuccessStatus(true)
                    setResponseMessage(userData.message)
                } else {
                    setRequestSuccessStatus(false)
                    setResponseMessage(userData.message)
                }
            }
        }
        detectUserChange()
    }, [userData, topRoleIsAccountHolder])

    const handleClickShowPassword = () => {
        setShowPassword((prev) => !prev)
    }

    const handleMouseDownPassword = (event) => {
        event.preventDefault()
    }

    const handleSnackbarOpenStatus = (status) => {
        setSnackbarOpen(status)
    }

    return (
        <>
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <form className={classes.root} noValidate autoComplete="off">
                    <div className={classes.paper}>
                        <Typography component="h1" variant="h4">
                            Login
                        </Typography>
                        <Grid container spacing={1} alignItems="flex-end">
                            <Grid item xs={12}>
                                <TextField
                                    className={classes.inputField}
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="email"
                                    label="Username or Email Address"
                                    name="email"
                                    autoComplete="email"
                                    autoFocus
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AccountCircle />
                                            </InputAdornment>
                                        ),
                                    }}
                                    error={!!errorMsg.username}
                                    helperText={errorMsg.username}
                                    onChange={(e) => {
                                        setUsername(e.target.value)
                                        setErrorMsg((prevErrorMsg) => ({
                                            ...prevErrorMsg,
                                            username: "",
                                        }))
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth variant="outlined" error={!!errorMsg.password} className={classes.inputField}>
                                    <InputLabel htmlFor="outlined-adornment-password">Password *</InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-password"
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => {
                                            setPassword(e.target.value)
                                            setErrorMsg((prevErrorMsg) => ({
                                                ...prevErrorMsg,
                                                password: "",
                                            }))
                                        }}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <LockIcon />
                                            </InputAdornment>
                                        }
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPassword}
                                                    onMouseDown={handleMouseDownPassword}
                                                    edge="end"
                                                >
                                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                        labelWidth={90}
                                    />
                                    <FormHelperText id="component-error-text">{errorMsg.password}</FormHelperText>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            className={classes.submit}
                            onClick={handleSubmit}
                        >
                            Login
                        </Button>
                        <Grid container>
                            <Grid item xs className={classes.centerText}>
                                <RouterLink to="/request-password-reset">Forgot password?</RouterLink>
                            </Grid>
                        </Grid>
                    </div>
                </form>
            </Container>
            {loading ? (
                <Loading />
            ) : (
                <RequestSnackbar
                    status={requestSuccessStatus}
                    message={responseMessage}
                    snackbarOpen={snackbarOpen}
                    handleSnackbarOpenStatus={handleSnackbarOpenStatus}
                />
            )}
        </>
    )
}
